import { useFormikContext } from "formik";
import React from "react";
import ConfirmSection from "../../../components/ConfirmSection";
import PageTitle from "../../../components/PageTitle";
import { FlatApplciationType } from "../../../types";
import { ChildDataType } from "csv-package";

const Confirm: React.FC = () => {
  const { values } = useFormikContext<FlatApplciationType>();

  return (
    <>
      <PageTitle
        heading="Confirm your details"
        description="Please review the details of your application to ensure that the information you've provided is true and correct."
      />

      <ConfirmSection
        heading="Applicant"
        goToStepIndex={0}
        config={[
          {
            heading: "Applicant details",
            names: [
              "appl_det_giv_nam",
              "appl_det_fam_nam",
              "appl_det_dob",
              "appl_det_sex",
              "appl_det_sex_other",
            ],
          },
          {
            private:
              "Private question - as the respondent, a court registrar will advise if your address is able to be kept secret if you have chosen this option.",
            names: [
              "address__appl_det_addr",
              "appl_det_addr_secret",
              "appl_det_court_heard",
              "CourtID"
            ],
          },
          {
            heading: "Court hearing",
            names: [
              "appl_det_court_in_person",
            ],
          },
          {
            heading: "Contact details",
            names: [
              "appl_safe_cont",
              "appl_cont_phone",
              "appl_cont_phone_2",
              "appl_cont_phone_3",
              "appl_cont_phone_vm",
              "appl_cont_safe_times",
              "appl_cont_email",
            ],
          },
        ]}
      />

      <ConfirmSection
        heading="Original order"
        goToStepIndex={1}
        config={[
          {
            names: ["eo_case_no", "eo_aprox_date", "eo_expiry"],
          },
        ]}
      />

      <ConfirmSection
        heading="Protected persons"
        goToStepIndex={2}
        config={[
          { names: ["prp_person"] },
          ...(values.protected_persons
            ? (values.protected_persons as ChildDataType[])
                .filter(({ prp_id }) => String(prp_id).startsWith("person"))
                .map((_, i) => ({
                  heading: `Protected person ${i + 1}`,
                  names: [
                    `protected_persons[${i}].prp_giv_nam`,
                    `protected_persons[${i}].prp_fam_nam`,
                    `protected_persons[${i}].prp_dob2`,
                    `protected_persons[${i}].prp_sex`,
                    `protected_persons[${i}].prp_sex_other`,
                    `protected_persons[${i}].seeking_appl_role`,
                    `protected_persons[${i}].seeking_appl_sub_role`,
                  ],
                }))
            : []),
          { heading: "Court Applications for Children", names: ["prp_ord_fc"] },
        ]}
      />

      <ConfirmSection
        heading="Respondent"
        goToStepIndex={3}
        config={[
          {
            names: [
              "resp_det_giv_nam",
              "resp_det_fam_nam",
              "resp_det_other_names",
              "address__resp_det_addr",
              "resp_cont_phone_3",
              "resp_cont_phone",
              "resp_cont_phone_2",
              "resp_cont_email",
              "resp_cont_other",
              "resp_weapon_access",
            ],
          },
        ]}
      />

      <ConfirmSection
        heading="Application type"
        goToStepIndex={4}
        config={[
          {
            names: ["app_types"],
          },
        ]}
      />

      <ConfirmSection
        heading="Application details"
        goToStepIndex={5}
        config={[
          {
            heading: "Extend",
            names: [
              "app_type_extend_further_violence_check",
              "app_type_extend_further_violence",
              "app_type_extend_why_breached",
              "app_type_extend_why_violence_stopped",
              "app_type_extend_still_fearful",
              "appl_pregnant_baby",
              "app_type_extend_duration",
            ],
            conditions: {
              app_types: "AE",
            },
          },
          {
            heading: "Vary",
            names: [
              "app_type_extend_further_violence_check",
              "app_type_extend_further_violence",
              "app_type_extend_why_breached",
              "app_type_extend_why_violence_stopped",
              "app_type_extend_still_fearful",
              "appl_pregnant_baby",
              "vary_resp_children",
            ],
            conditions: {
              app_types: "AV",
            },
          },
          ...(values.children
            ? values.children
                .filter(({ prp_id }) => String(prp_id).includes("child"))
                .map(({ child_action }, i) => ({
                  heading: `${child_action === "add" ? "Add" : "Remove"} child`,
                  names: [
                    `children[${i}].prp_giv_nam`,
                    `children[${i}].prp_giv_nam2`,
                    `children[${i}].prp_fam_nam`,
                    `children[${i}].prp_dob`,
                    ...(child_action === "add"
                      ? [
                          `children[${i}].prp_live`,
                          `children[${i}].prp_rel_to_appl`,
                          `children[${i}].prp_rel_to_appl_other`,
                          `children[${i}].prp_rel_to_resp`,
                          `children[${i}].prp_rel_to_resp_other`,
                          `children[${i}].prp_sex1`,
                          `children[${i}].prp_sex_other`,
                          `children[${i}].prp_abor_code`,
                          `children[${i}].prp_disabl`,
                          `children[${i}].prp_disabl_req_other`,
                          `children[${i}].prp_ord_exist`,
                        ]
                      : []),
                  ],
                  conditions: {
                    app_types: "AV",
                  },
                }))
            : []),
          {
            heading:
              "What conditions do you want to change on the Family Violence Intervention Order?",
            names: ["vary_resp_other"],
            conditions: {
              app_types: "AV",
            },
          },
          {
            heading: "Revoke",
            names: ["revoke_appl_why"],
            conditions: {
              app_types: "AR",
            },
          },
        ]}
      />
    </>
  );
};

export default Confirm;
